import { FormPageType } from "../context/formData.reducer";
import { Week } from "../context/linkData.reducer";
import { isWeekLockedForSession, WeekLockContext } from "./isWeekLocked";

const isSavedSession = (session: { id?: string; isNew?: boolean; isCloned?: boolean }): boolean =>
  !!session?.id && !session.id.includes("-") && !session?.isNew && !session?.isCloned;

/**
 * `forkSelectedSessions` derives the weeks that stay behind from the FIRST
 * saved selected session only, so forking a heterogeneous multi-selection would
 * split the remaining sessions against the wrong week set.
 */
const haveSameOriginalWeeks = (
  savedSessions: { id?: string }[],
  originalWeeksBySessionId: { [sessionId: string]: string[] },
): boolean => {
  if (savedSessions.length < 2) return true;

  const [firstWeeks, ...restWeeks] = savedSessions.map(session =>
    (originalWeeksBySessionId?.[session?.id] ?? []).join("|"),
  );

  return restWeeks.every(weeks => weeks === firstWeeks);
};

/**
 * The week ids a previous fork already moved out of the selected sessions.
 *
 * `originalSelectedWeekIds` comes from the backend assignment, so it knows
 * nothing about a fork performed in local state. Without this subtraction, a
 * session whose weeks were just forked away looks exactly like one whose weeks
 * the user just unchecked, and the card would offer to fork it again with no
 * further edit.
 */
const getAlreadyForkedWeekIds = (form: FormPageType): { [weekId: string]: boolean } => {
  const selectedSessionIds = new Set(
    (form?.selectedSessions ?? []).map(session => session?.id).filter(Boolean),
  );

  const result: { [weekId: string]: boolean } = {};

  (form?.sessionsToCreate ?? []).forEach(session => {
    Object.entries(session?.forkedWeeksByOriginSessions ?? {}).forEach(
      ([originSessionId, weekIds]) => {
        if (!selectedSessionIds.has(originSessionId)) return;

        (weekIds ?? []).forEach(weekId => {
          result[weekId] = true;
        });
      },
    );
  });

  return result;
};

/**
 * Unchecking a week is the trigger on its own: editing a resource is NOT
 * required, unlike the validate-time modal (`needsPastFork`).
 */
export const canForkSelectedSessions = (
  form: FormPageType,
  lockContext: WeekLockContext,
  originalWeeksBySessionId: { [sessionId: string]: string[] },
): boolean => {
  const intervals = form?.editedSessions?.intervals ?? [];
  const alreadyForkedWeekIds = getAlreadyForkedWeekIds(form);
  const selectedSessions = lockContext.selectedSessions?.length
    ? lockContext.selectedSessions
    : (form?.selectedSessions ?? []);
  const savedSessions = selectedSessions.filter(isSavedSession);
  if (!savedSessions.length) return false;

  // A week a previous fork already moved out is NOT movable again: forking it
  // a second time would leave two new sessions covering the same week, day and
  // blocks, which only surfaces later as a clash at validation.
  const hasMovableWeek = intervals.some(
    (week: Week) =>
      !!week?.checked &&
      savedSessions.some(session => !isWeekLockedForSession(week, session, lockContext)) &&
      !alreadyForkedWeekIds[week.id],
  );
  if (!hasMovableWeek) return false;

  const checkedById = intervals.reduce<{ [id: string]: boolean }>((acc, week: Week) => {
    acc[week.id] = !!week?.checked;
    return acc;
  }, {});

  const hasNewlyUncheckedAssignedWeek = Object.keys(lockContext.originalSelectedWeekIds ?? {}).some(
    weekId => !checkedById[weekId] && !alreadyForkedWeekIds[weekId],
  );
  if (!hasNewlyUncheckedAssignedWeek) return false;

  if (lockContext.hasDeletedSessionSelected) return false;

  return haveSameOriginalWeeks(savedSessions, originalWeeksBySessionId);
};
